import ProductProvider from "./productProvider";
import Big from "big.js";

const Product = class {
    constructor(object) {
        if (object === undefined) {
            this.provider = new ProductProvider();
            // Categoria: dimension opcional. Contabilidad la usa para separar
            // ventas, inventario y costo por tipo de producto.
            this.category = null;
            this.id = null;
            this.code = null;
            this.name = null;
            this.hasTax = false;
            // Impuestos SRI. `ivaCode` es el codigoPorcentaje del catalogo del
            // SRI (0=0%, 5=5%, 4=15%); es la fuente de verdad y `hasTax` queda
            // derivado. Default 0% para no cambiar el comportamiento previo.
            this.ivaCode = '0';
            this.hasIce = false;
            this.iceCode = null;
            this.iceType = null;
            this.iceRate = null;
            this.iceSpecificRate = null;
            this.discount = new Big('0.0');
            this.hasStock = false;
            this.hasComposition = false;
            this.operatorSyncable = false;
            // Uso: un insumo (pan, sal) no se vende; uno periódico solo lo bajan los
            // conteos. Defaults = comportamiento de siempre.
            this.isSellable = true;
            this.stockMode = 'PERPETUAL';
            this.parLevel = null;
            this.qFactorType = null;
            this.qFactorValue = null;
            this.stock = null;
            this.selectedAmount = 1; // Used in product menu
            this.imageUrl = null;
            this.isPrintable = false;

            this.price = new Big('0.0');
            this.price2 = null;
            this.price3 = null;
            this.price4 = null;
            this.price5 = null;
            this.currentCost = null;
            this.qFactorCost = null;
            this.recipeCost = null;
        } else {
            this.provider = new ProductProvider(object.provider)
            this.category = object.category || null;
            this.id = object.id || null
            this.code = object.code || object.id;
            this.name = object.name || null;
            this.hasTax = object.hasTax || false;
            // El backend ya envia ivaCode resuelto; el fallback cubre payloads
            // legacy que solo traian el binario hasTax.
            this.ivaCode = object.ivaCode || (object.hasTax ? '4' : '0');
            this.hasIce = object.hasIce || false;
            this.iceCode = object.iceCode || null;
            this.iceType = object.iceType || null;
            this.iceRate = (object.iceRate !== undefined && object.iceRate !== null) ? object.iceRate : null;
            this.iceSpecificRate = (object.iceSpecificRate !== undefined && object.iceSpecificRate !== null) ? object.iceSpecificRate : null;
            this.discount = (object.discount !== undefined) ? new Big(object.discount) : new Big('0.0');
            this.hasStock = object.hasStock || false;
            this.hasComposition = object.hasComposition || false;
            // El operador puede sincronizar su stock (merma natural: barril, granel).
            this.operatorSyncable = object.operatorSyncable || false;
            this.isSellable = object.isSellable !== false;
            this.stockMode = object.stockMode || 'PERPETUAL';
            this.parLevel = (object.parLevel !== undefined && object.parLevel !== null) ? Number(object.parLevel) : null;
            this.qFactorType = object.qFactorType || null;
            this.qFactorValue = (object.qFactorValue !== undefined && object.qFactorValue !== null) ? Number(object.qFactorValue) : null;
            this.stock = (object.stock !== undefined && object.stock !== null) ? new Big(object.stock) : null;
            this.selectedAmount = object.selectedAmount || 1; // Used in product menu
            this.imageUrl = object.imageUrl || null;
            this.isPrintable = object.isPrintable || false;

            this.price = (object.price !== undefined && object.price !== null) ? new Big(object.price) : null;
            this.price2 = (object.price2 !== undefined && object.price2 !== null) ? new Big(object.price2) : null;
            this.price3 = (object.price3 !== undefined && object.price3 !== null) ? new Big(object.price3) : null;
            this.price4 = (object.price4 !== undefined && object.price4 !== null) ? new Big(object.price4) : null;
            this.price5 = (object.price5 !== undefined && object.price5 !== null) ? new Big(object.price5) : null;

            // currentCost: costo FIFO unitario calculado on-the-fly por el
            // backend (solo lo expone a usuarios admin). Se usa para mostrar
            // utilidad estimada al agregar productos nuevos a una venta antes
            // de guardarla. Null si el backend no lo envió.
            this.currentCost = (object.currentCost !== undefined && object.currentCost !== null) ? new Big(object.currentCost) : null;
            // Costo de receta = componentes + factor Q (solo para costear en la ficha).
            this.qFactorCost = (object.qFactorCost !== undefined && object.qFactorCost !== null) ? new Big(object.qFactorCost) : null;
            this.recipeCost = (object.recipeCost !== undefined && object.recipeCost !== null) ? new Big(object.recipeCost) : null;
        }
        // For menu animation purposes
        this.productSelected = false;
    }
    // ICE estimado de una linea, espejo de Product.computeIce del backend. Sirve
    // para que el total en pantalla de un item recien agregado (aun sin guardar)
    // coincida con el que calculara el servidor.
    estimateIce(quantity, base) {
        if (!this.hasIce) { return new Big('0.0'); }
        let total = new Big('0.0');
        if ((this.iceType === 'SPECIFIC' || this.iceType === 'MIXED') && this.iceSpecificRate) {
            total = total.plus(new Big(this.iceSpecificRate).times(quantity));
        }
        if ((this.iceType === 'AD_VALOREM' || this.iceType === 'MIXED') && this.iceRate) {
            total = total.plus(new Big(base).times(this.iceRate).div(100));
        }
        return total.round(2);
    }

    get priceList() {
        var list = [this.price];
        if (this.price2 !== null && this.price2 !== undefined) { list.push(this.price2); }
        if (this.price3 !== null && this.price3 !== undefined) { list.push(this.price3); }
        if (this.price4 !== null && this.price4 !== undefined) { list.push(this.price4); }
        if (this.price5 !== null && this.price5 !== undefined) { list.push(this.price5); }
        return list;
    }

    get priceMap() {
        var list = [{label: this.price}];
        if (this.price2 !== null && this.price2 !== undefined) { list.push({label: this.price2}); }
        if (this.price3 !== null && this.price3 !== undefined) { list.push({label: this.price3}); }
        if (this.price4 !== null && this.price4 !== undefined) { list.push({label: this.price4}); }
        if (this.price5 !== null && this.price5 !== undefined) { list.push({label: this.price5}); }
        return list;
    }

    // La categoria llega serializada como objeto ({id, name, active}); el
    // Dropdown editable de productCRUD tambien permite escribir un nombre
    // suelto, asi que aceptamos ambas formas.
    get categoryName() {
        if (this.category === null || this.category === undefined) { return null; }
        return (typeof this.category === 'object') ? (this.category.name || null) : this.category;
    }

    get stockBadge() {
        if (this.hasStock) {
            if (this.stock <= 0) {
                return "outofstock"
            } else if (this.stock > 0 && this.stock <= 8) {
                return "lowstock"
            } else {
                return "instock"
            }
        } else { return "" }
    }

    get nameAsSuggestions() {
        if (!this.name) return '';
        const text = `${this.code ? this.code : ''}${this.code ? ' | ' : ''}${this.name ? this.name : ''}`;
        const maxLen = 40;
        return text.length > maxLen ? text.slice(0, maxLen) + '...' : text;
    }
}

export default Product